import React, { useState, useEffect, useRef } from "react";
import { Typography, Box } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { useRccTypography } from "../typography";
import { rccColors } from "../colors";
import { appService } from "../../App/app.service";
import { useTextScale } from "../hooks/useTextScale";

const useStyles = makeStyles((theme) => ({
  card: {
    display: "flex",
    padding: "16px",
    alignItems: "flex-start",
    gap: "12px",
    flex: "1 0 0",
    alignSelf: "stretch",
    borderRadius: "12px",
    border: `1px solid ${rccColors.blue[900]}`,
    background: `linear-gradient(100deg, #FFF 8.18%, ${rccColors.blue[50]} 92.51%)`,
    boxShadow: "6px 6px 54px 0 rgba(0, 0, 0, 0.05)",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
  },
  contentRoot: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    alignItems: "flex-start",
    flex: "1 1 auto",
    alignSelf: "stretch",
    height: "100%",
    paddingBottom: "28px",
  },
  titleContainer: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    alignSelf: "stretch",
    paddingBottom: "12px",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    alignItems: "flex-start",
    flex: "1 1 auto",
    alignSelf: "stretch",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
  },
  checkIcon: {
    color: rccColors.green[500],
    width: "var(--icon-size, 14px)",
    height: "var(--icon-size, 14px)",
    fontSize: "var(--icon-size, 14px)",
    aspectRatio: "1/1",
  },
  warningIcon: {
    color: rccColors.orange[500],
    width: "var(--icon-size, 14px)",
    height: "var(--icon-size, 14px)",
    fontSize: "var(--icon-size, 14px)",
    aspectRatio: "1/1",
  },
  insightList: {
    listStyle: "disc",
    paddingLeft: theme.spacing(2),
    margin: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    flex: "1 1 auto",
    width: "100%",
  },
  insightItem: {
    "&::marker": {
      color: rccColors.neutral[300],
    },
  },
  emptyState: {
    color: rccColors.neutral[500],
    fontSize: "1rem",
    fontStyle: "italic",
    textAlign: "center",
    padding: theme.spacing(2),
  },
  errorState: {
    color: rccColors.red[600],
    fontSize: "1rem",
    textAlign: "center",
    padding: theme.spacing(2),
  },
  fadeOverlay: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "64px",
    background:
      "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(227, 242, 253, 0.92) 55%, #E3F2FD 100%)",
    pointerEvents: "none",
    zIndex: 1,
  },
  readMorePinned: {
    position: "absolute",
    right: "16px",
    bottom: "12px",
    zIndex: 2,
  },
}));

function PerformanceInsightsCard({ title, redirectToDashboard }) {
  const classes = useStyles();
  const typography = useRccTypography();
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchingRef = useRef(false);

  // Dynamic text scaling based on container vs content size
  // Text scales 12-16px for list items, spacing handled by flexbox
  const { containerRef, contentRef, cssVars } = useTextScale({
    dependencies: [data, loading],
  });

  useEffect(() => {
    if (fetchingRef.current) return;

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    appService.getPerformanceInsights().then(
      (response) => {
        fetchingRef.current = false;
        if (response.success && response.data) {
          setData(response.data);
        } else {
          setError("Failed to load performance insights");
        }
        setLoading(false);
      },
      (err) => {
        fetchingRef.current = false;
        setError(err?.message || "Failed to load performance insights");
        setLoading(false);
      }
    );
  }, []);

  // Show loading state
  if (loading) {
    return (
      <Box className={classes.card}>
        <Box
          className={classes.contentRoot}
          style={{ justifyContent: "flex-start", gap: "16px" }}
        >
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton
            variant="rect"
            width="100%"
            height={200}
            style={{ borderRadius: "8px" }}
          />
        </Box>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box className={classes.card}>
        <Box
          className={classes.contentRoot}
          style={{ justifyContent: "flex-start", gap: "16px" }}
        >
          <Box className={classes.titleContainer}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M7 0C7 0 7.38623 3.60496 8.89064 5.10936C10.395 6.61377 14 7 14 7C14 7 10.395 7.38623 8.89064 8.89064C7.38623 10.395 7 14 7 14C7 14 6.61377 10.395 5.10936 8.89064C3.60496 7.38623 0 7 0 7C0 7 3.60496 6.61377 5.10936 5.10936C6.61377 3.60496 7 0 7 0Z"
                fill="url(#paint0_linear_performance_insights)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_performance_insights"
                  x1="7"
                  y1="0"
                  x2="7"
                  y2="8.16667"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#80DEEA" />
                  <stop offset="1" stopColor="#1976D2" />
                </linearGradient>
              </defs>
            </svg>
            <Typography
              style={{
                color: rccColors.blue[700],
                fontSize: "1.5rem",
                fontWeight: 600,
                fontFamily: '"Onest", sans-serif',
              }}
            >
              {title || "Performance Insights"}
            </Typography>
          </Box>
          <Typography className={classes.errorState}>{error}</Typography>
        </Box>
      </Box>
    );
  }

  // Show empty state
  if (!data || Object.keys(data).length === 0) {
    return (
      <Box className={classes.card}>
        <Box
          className={classes.contentRoot}
          style={{ justifyContent: "flex-start", gap: "16px" }}
        >
          <Box className={classes.titleContainer}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M7 0C7 0 7.38623 3.60496 8.89064 5.10936C10.395 6.61377 14 7 14 7C14 7 10.395 7.38623 8.89064 8.89064C7.38623 10.395 7 14 7 14C7 14 6.61377 10.395 5.10936 8.89064C3.60496 7.38623 0 7 0 7C0 7 3.60496 6.61377 5.10936 5.10936C6.61377 3.60496 7 0 7 0Z"
                fill="url(#paint0_linear_performance_insights)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_performance_insights"
                  x1="7"
                  y1="0"
                  x2="7"
                  y2="8.16667"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#80DEEA" />
                  <stop offset="1" stopColor="#1976D2" />
                </linearGradient>
              </defs>
            </svg>
            <Typography
              style={{
                color: rccColors.blue[700],
                fontSize: "1.5rem",
                fontWeight: 600,
                fontFamily: '"Onest", sans-serif',
              }}
            >
              {title || "Performance Insights"}
            </Typography>
          </Box>
          <Typography className={classes.emptyState}>
            No performance insights available at this time.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Extract data from response.
  // Backward compatibility: support both legacy keys and new enum keys while the API transitions.
  const normalizeInsightItems = (value, sourceKey) => {
    if (!Array.isArray(value)) return [];

    return value
      .map((item, idx) => {
        if (typeof item === "string") {
          return { id: `${sourceKey}-${idx}`, description: item };
        }
        if (!item || typeof item !== "object") return null;

        const description =
          typeof item.description === "string"
            ? item.description
            : typeof item.Diagnostic_Summary === "string"
            ? item.Diagnostic_Summary
            : typeof item.diagnosticSummary === "string"
            ? item.diagnosticSummary
            : typeof item.text === "string"
            ? item.text
            : null;

        if (!description) return null;

        return {
          ...item,
          id: item.id ?? `${sourceKey}-${idx}`,
          description,
        };
      })
      .filter(Boolean);
  };

  const mergeInsights = (primaryKey, legacyKey) => {
    const primary = normalizeInsightItems(data?.[primaryKey], primaryKey);
    const legacy = normalizeInsightItems(data?.[legacyKey], legacyKey);
    return primary.length > 0 ? primary : legacy;
  };

  const whatsGoingWell = mergeInsights(
    "BETTER_THAN_EXPECTED",
    "What's Going Well"
  );
  const needsAttention = mergeInsights(
    "WORSE_THAN_EXPECTED",
    "Needs Attention"
  );

  // Cap: show max 8 pointers total.
  // - If BOTH categories exist, show up to 4 per category, then fill any remaining
  //   slots (to reach 8) from the category that has more items remaining.
  // - If only ONE category exists, show up to 8 from that category.
  const MAX_TOTAL_POINTERS = 8;
  const MAX_PER_CATEGORY_POINTERS = 4;

  const originalNeedsAttentionCount = Array.isArray(needsAttention)
    ? needsAttention.length
    : 0;
  const originalWhatsGoingWellCount = Array.isArray(whatsGoingWell)
    ? whatsGoingWell.length
    : 0;
  const originalTotalCount =
    originalNeedsAttentionCount + originalWhatsGoingWellCount;

  const hasBothCategories =
    originalNeedsAttentionCount > 0 && originalWhatsGoingWellCount > 0;

  // Start with up to 4 per category when both exist.
  const baseNeeds = hasBothCategories
    ? Math.min(originalNeedsAttentionCount, MAX_PER_CATEGORY_POINTERS)
    : Math.min(originalNeedsAttentionCount, MAX_TOTAL_POINTERS);
  const baseWell = hasBothCategories
    ? Math.min(originalWhatsGoingWellCount, MAX_PER_CATEGORY_POINTERS)
    : Math.min(originalWhatsGoingWellCount, MAX_TOTAL_POINTERS);

  // Fill remaining slots (only meaningful when both exist).
  const baseTotal = baseNeeds + baseWell;
  const remaining = Math.max(0, MAX_TOTAL_POINTERS - baseTotal);
  const remainingNeeds = Math.max(0, originalNeedsAttentionCount - baseNeeds);
  const remainingWell = Math.max(0, originalWhatsGoingWellCount - baseWell);

  const extraNeeds = hasBothCategories
    ? Math.min(remaining, remainingNeeds)
    : 0;
  const extraWell = hasBothCategories
    ? Math.min(Math.max(0, remaining - extraNeeds), remainingWell)
    : 0;

  const needsAttentionLimited = Array.isArray(needsAttention)
    ? needsAttention.slice(0, baseNeeds + extraNeeds)
    : [];

  const whatsGoingWellLimited = Array.isArray(whatsGoingWell)
    ? whatsGoingWell.slice(0, baseWell + extraWell)
    : [];

  const hasWhatsGoingWell = whatsGoingWellLimited.length > 0;
  const hasNeedsAttention = needsAttentionLimited.length > 0;
  const hasAnyInsights = hasWhatsGoingWell || hasNeedsAttention;
  const isTruncated =
    needsAttentionLimited.length + whatsGoingWellLimited.length <
    originalTotalCount;

  const handleReadMore = () => {
    // Use the same redirect mechanism as RCC cards (localStorage + reload).
    // This navigates to "Other Insights" and selects the "Performance Alerts" view.
    localStorage.setItem("skygeni-user-page", "Other Insights");
    localStorage.setItem("skygeni-user-othersPage", "Performance Alerts");

    // Set RCC source marker for "Back to RCC" button parity.
    sessionStorage.setItem("skygeni-rcc-source", "true");

    // Remove other page selections to avoid conflicting navigation state.
    const keysToClear = [
      "enhancedPage",
      "repPage",
      "marketPage",
      "growthModelingPage",
      "pipelineBuildandSuficencyPage",
      "pipelineVelocityandConversionPage",
      "marketIntelandGrowthLeversPage",
      "salesProductivityPage",
      "growthScienceInsightsPage",
      "personalizedScorecardsPage",
      "SettingsPage",
      "productPage",
    ];
    keysToClear.forEach((k) => localStorage.removeItem(`skygeni-user-${k}`));

    history.push("/");
    window.location.reload();
  };

  return (
    <Box className={classes.card} ref={containerRef} style={cssVars}>
      <Box className={classes.contentRoot} ref={contentRef}>
        {/* Title with custom icon */}
        <Box className={classes.titleContainer}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
          >
            <path
              d="M7 0C7 0 7.38623 3.60496 8.89064 5.10936C10.395 6.61377 14 7 14 7C14 7 10.395 7.38623 8.89064 8.89064C7.38623 10.395 7 14 7 14C7 14 6.61377 10.395 5.10936 8.89064C3.60496 7.38623 0 7 0 7C0 7 3.60496 6.61377 5.10936 5.10936C6.61377 3.60496 7 0 7 0Z"
              fill="url(#paint0_linear_performance_insights)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_performance_insights"
                x1="7"
                y1="0"
                x2="7"
                y2="8.16667"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#80DEEA" />
                <stop offset="1" stopColor="#1976D2" />
              </linearGradient>
            </defs>
          </svg>
          <Typography
            style={{
              color: rccColors.blue[700],
              fontSize: "var(--title-size, 20px)",
              fontWeight: 600,
              fontFamily: '"Onest", sans-serif',
              flex: 1,
            }}
          >
            {title || "Performance Insights"}
          </Typography>
          {redirectToDashboard && (
            <i
              className="material-icons"
              style={{
                cursor: "pointer",
                color: rccColors.neutral[400],
                fontSize: "20px",
              }}
              onClick={() => {
                /* handle redirect */
              }}
            >
              arrow_outward
            </i>
          )}
        </Box>

        {!hasAnyInsights ? (
          <Typography className={classes.emptyState}>
            No performance insights available at this time.
          </Typography>
        ) : (
          <>
            {/* What's Going Well Section */}
            {hasWhatsGoingWell && (
              <Box className={classes.section}>
                <Box className={classes.sectionTitle}>
                  <i className={`material-icons ${classes.checkIcon}`}>
                    check_circle
                  </i>
                  <Typography
                    style={{
                      color: rccColors.neutral[900],
                      fontSize: "var(--section-title-size, 16px)",
                      fontWeight: 600,
                      fontFamily: '"Onest", sans-serif',
                    }}
                  >
                    What's Going Well
                  </Typography>
                </Box>
                <ul className={classes.insightList}>
                  {whatsGoingWellLimited.map((insight) => (
                    <li key={insight.id} className={classes.insightItem}>
                      <Typography
                        style={{
                          color: rccColors.neutral[700],
                          fontSize: "var(--item-size, 14px)",
                          fontWeight: 400,
                          fontFamily: '"Onest", sans-serif',
                        }}
                      >
                        {insight.description}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            )}

            {/* Needs Attention Section */}
            {hasNeedsAttention && (
              <Box className={classes.section}>
                <Box className={classes.sectionTitle}>
                  <i className={`material-icons ${classes.warningIcon}`}>
                    error
                  </i>
                  <Typography
                    style={{
                      color: rccColors.neutral[900],
                      fontSize: "var(--section-title-size, 16px)",
                      fontWeight: 600,
                      fontFamily: '"Onest", sans-serif',
                    }}
                  >
                    Needs Attention
                  </Typography>
                </Box>
                <ul className={classes.insightList}>
                  {needsAttentionLimited.map((insight) => (
                    <li key={insight.id} className={classes.insightItem}>
                      <Typography
                        style={{
                          color: rccColors.neutral[700],
                          fontSize: "var(--item-size, 14px)",
                          fontWeight: 400,
                          fontFamily: '"Onest", sans-serif',
                        }}
                      >
                        {insight.description}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            )}

            <Box className={classes.fadeOverlay} />
            <Box className={classes.readMorePinned}>
              <Typography
                className={typography.textSmallSemibold}
                style={{
                  cursor: "pointer",
                  color: rccColors.blue[700],
                  fontSize: "var(--item-size, 14px)",
                  fontFamily: '"Onest", sans-serif',
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                onClick={handleReadMore}
              >
                Read more
                <i
                  className="material-icons"
                  style={{
                    color: rccColors.blue[700],
                    fontSize: "var(--item-size, 14px)",
                  }}
                >
                  arrow_outward
                </i>
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

export default PerformanceInsightsCard;
